/**
 * Patient-facing HIPAA transparency card — "Who has accessed my record?"
 *
 * Pulls from /api/phi/access-log which returns the last 50 staff/clinician
 * views of the signed-in patient's record. Fulfils the 21st Century Cures
 * Act "right of access" expectation and differentiates GLPFlo as a
 * patient-friendly telehealth platform.
 */
import { useCallback, useEffect, useState } from "react";
import API from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Eye, RefreshCw, ChevronDown, Download } from "lucide-react";

const ACTION_LABEL = {
  phi_access: "Viewed record",
  phi_reveal: "Revealed sensitive field",
  view_patient_record: "Viewed record",
};

const RESOURCE_LABEL = {
  patient_chart: "Patient chart",
  patient_profile: "Profile details",
  patient_notes: "Clinical notes",
  patient_documents: "Documents",
  wellness_logs: "Wellness logs",
  patient_dob: "Date of birth",
  patient_ssn: "Last-4 of SSN",
  patient_full_address: "Full address",
  patient_phone: "Phone number",
};

const ROLE_COPY = {
  admin: "Admin",
  prescriber: "Your prescriber",
  customer_service: "Support team",
  it: "Tech support",
};

export default function PhiAccessCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/phi/access-log?limit=50");
      setRows(data?.entries || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadMyData = async () => {
    setExporting(true);
    try {
      const resp = await API.get("/compliance/my-data", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `glpflo-my-data-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // Refresh the access log so the user sees their own export event.
      setTimeout(load, 500);
    } catch {
      // toast already surfaced by API interceptor
    } finally {
      setExporting(false);
    }
  };

  const preview = expanded ? rows : rows.slice(0, 5);

  return (
    <Card className="border-purple-100" data-testid="phi-access-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield className="w-4 h-4 text-purple-600" />
          Who has accessed my record?
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          We log every time a clinician, admin, or support teammate views your record.
          You can see exactly who, when, and what they looked at.
        </p>
        <button
          type="button"
          onClick={downloadMyData}
          disabled={exporting}
          className="mt-3 inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full border border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-800 text-xs font-semibold transition-colors disabled:opacity-60"
          data-testid="phi-access-export-btn"
        >
          <Download className="w-3.5 h-3.5" />
          {exporting ? "Preparing your archive…" : "Download all my data (HIPAA § 164.524)"}
        </button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="rounded-lg bg-slate-50 p-4 text-center text-sm text-slate-600" data-testid="phi-access-empty">
            No staff has accessed your record yet. You&apos;ll see an entry here the moment anyone does.
          </div>
        ) : (
          <>
            <ul className="space-y-2" data-testid="phi-access-list">
              {preview.map((r, i) => (
                <li
                  key={`${r.timestamp}-${i}`}
                  className="flex items-start gap-3 text-sm rounded-lg border border-slate-100 px-3 py-2 hover:border-purple-200 transition-colors"
                  data-testid="phi-access-row"
                >
                  <Eye className="w-4 h-4 text-purple-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-800">
                      <strong className="text-slate-900">{ROLE_COPY[r.viewer_role] || r.viewer_role || "Staff"}</strong>
                      {" · "}
                      {ACTION_LABEL[r.action] || r.action}
                      <span className="text-slate-500"> — {RESOURCE_LABEL[r.resource_type] || r.resource_type}</span>
                    </p>
                    {r.reason && (
                      <p className="text-xs text-slate-500 truncate italic">
                        {r.reason}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-slate-500 whitespace-nowrap shrink-0">
                    {new Date(r.timestamp).toLocaleString(undefined, {
                      month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
                    })}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 mt-3">
              {rows.length > 5 && (
                <button
                  type="button"
                  onClick={() => setExpanded((e) => !e)}
                  className="text-xs text-purple-700 font-semibold hover:underline inline-flex items-center gap-1"
                  data-testid="phi-access-expand"
                >
                  {expanded ? `Show less` : `Show all ${rows.length} entries`}
                  <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
                </button>
              )}
              <button
                type="button"
                onClick={load}
                className="ml-auto text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                data-testid="phi-access-refresh"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
